import { useState, useEffect, useLayoutEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Menu,
  Drawer,
  Avatar,
  Dropdown,
  Badge,
  Tooltip,
  ConfigProvider,
} from 'antd';
import {
  Menu as MenuIcon,
  LayoutDashboard,
  Store,
  Users,
  Star,
  FileCode,
  Gift,
  // Bell,
  BarChart3,
  FileSpreadsheet,
  Settings,
  Plus,
  TrendingUp,
  Trophy,
  Mail,
  Shield,
  ChevronLeft,
  LogOut,
  UserCircle,
  Database,
  Package,
  Sliders,
  ShoppingBag,
  Puzzle,
  History,
  ScanSearch,
  Tag,
  HelpCircle,
  Book,
  FileText,
  MessageCircle,
  MessageCircleQuestionMark,
  CloudSync,
  Share2,
  Send,
  Ticket,
  Car,
} from 'lucide-react';
import addressCafeLogo from '@/assets/images/logo.png';
import onlyLogo from '/images/only_logo.png';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { logout } from '@store/slices/authSlice';
import LanguageSwitcher from '@components/common/LanguageSwitcher';
import { useAccessControl } from '@/hooks/useAccessControl';
import { APP_VERSION } from '@utils';

const { Header, Sider, Content } = Layout;

/**
 * Address Cafe Loyalty Platform - Admin Layout Component
 */

// Sidebar dimensions for open and collapsed states
const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const AdminLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { direction, isRTL } = useAppSelector((state) => state.language);
  const unreadTotal = useAppSelector((state) => state.support.unreadTotal);
  const { hasModuleAccess } = useAccessControl();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);

  // Close drawers when direction changes to prevent wrong-side opening
  useEffect(() => {
    setDrawerOpen(false);
    setMobileOpen(false);
  }, [direction]);

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
    setDrawerOpen(false);
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const handleOpenChange = (keys) => {
    // Only allow one section to be open at a time (accordion behavior)
    const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1);
    if (latestOpenKey) {
      setOpenKeys([latestOpenKey]);
    } else {
      setOpenKeys([]);
    }
  };

  /**
   * Navigation Menu Structure
   * Module codes match the access_control API response
   */
  const navSections = [
    {
      key: 'overview',
      title: t('Overview'),
      items: [
        // No module: every staff member lands on the Dashboard after login.
        { text: t('Dashboard'), icon: <LayoutDashboard className="w-5 h-5" />, path: '/dashboard' },
        {
          text: t('Analytics'),
          icon: <BarChart3 className="w-5 h-5" />,
          path: '/analytics',
          moduleCode: 'ANALYTICS',
        },
        {
          text: t('Reports'),
          icon: <FileSpreadsheet className="w-5 h-5" />,
          path: '/reports',
          moduleCode: 'ANALYTICS',
        },
        {
          text: t('Link Insights Console'),
          icon: <Share2 className="w-5 h-5" />,
          path: '/share-link',
          moduleCode: 'ANALYTICS',
        },
      ],
    },
    {
      key: 'member-management',
      title: t('Member Management'),
      items: [
        {
          text: t('Users & Members'),
          icon: <Users className="w-5 h-5" />,
          path: '/users',
          moduleCode: 'USERS',
        },
        {
          text: t('Member Tiers'),
          icon: <Trophy className="w-5 h-5" />,
          path: '/tiers',
          moduleCode: 'TIERS',
        },
        {
          text: t('Points Management'),
          icon: <Star className="w-5 h-5" />,
          path: '/points',
          moduleCode: 'POINTS',
        },
        // {
        //   text: t('Earning Rules'),
        //   icon: <TrendingUp className="w-5 h-5" />,
        //   path: '/earning-rules',
        //   moduleCode: 'EARNING_RULES',
        // },
        {
          text: t('System Config'),
          icon: <Sliders className="w-5 h-5" />,
          path: '/points-config',
          moduleCode: 'SYSTEM_CONFIG',
        },
      ],
    },
    {
      key: 'engagement-growth',
      title: t('Engagement & Growth'),
      items: [
        // {
        //   text: t('Rewards Catalog'),
        //   icon: <Gift className="w-5 h-5" />,
        //   path: '/rewards',
        //   moduleCode: 'REWARDS',
        // },
        {
          text: t('Gamification Config'),
          icon: <Trophy className="w-5 h-5" />,
          path: '/gamification-config',
          moduleCode: 'GAMIFICATION',
        },
        {
          text: t('Push Broadcast'),
          icon: <Send className="w-5 h-5" />,
          path: '/broadcast',
          moduleCode: 'NOTIFICATIONS',
        },
        // {
        //   text: t('Content Management'),
        //   icon: <FileCode className="w-5 h-5" />,
        //   path: '/cms',
        //   moduleCode: 'CMS',
        // },
        {
          text: t('Offer Management'),
          icon: <Tag className="w-5 h-5" />,
          path: '/offers',
          moduleCode: 'OFFERS',
        },
        {
          text: t('Car Catalog'),
          icon: <Car className="w-5 h-5" />,
          path: '/car-catalog',
          moduleCode: 'CONTENT_MANAGEMENT',
        },
      ],
    },
    // {
    //   key: 'automation-rules',
    //   title: t('Automation & Rules'),
    //   items: [
    //     {
    //       text: t('Communication'),
    //       icon: <Mail className="w-5 h-5" />,
    //       path: '/communication',
    //       moduleCode: 'CAMPAIGNS',
    //     },
    //     {
    //       text: t('Notifications'),
    //       icon: <Bell className="w-5 h-5" />,
    //       path: '/notifications',
    //       badge: 3,
    //       moduleCode: 'NOTIFICATIONS',
    //     },
    //   ],
    // },
    {
      key: 'employee-management',
      title: t('Employee Management'),
      items: [
        {
          text: t('Employee List'),
          icon: <Users className="w-5 h-5" />,
          path: '/employees',
          moduleCode: 'EMPLOYEES',
        },
        {
          text: t('Employee Points'),
          icon: <Star className="w-5 h-5" />,
          path: '/employee-points',
          moduleCode: 'EMPLOYEE_POINTS',
        },
        // {
        //   text: t('Meal Points'),
        //   icon: <Star className="w-5 h-5" />,
        //   path: '/meal-points',
        //   moduleCode: 'MEAL_POINTS',
        // },
      ],
    },
    {
      key: 'orders-management',
      title: t('Orders Management'),
      items: [
        {
          text: t('Orders'),
          icon: <ShoppingBag className="w-5 h-5" />,
          path: '/orders',
          moduleCode: 'ORDERS',
        },
        {
          text: t('Coupons'),
          icon: <Ticket className="w-5 h-5" />,
          path: '/coupons',
          moduleCode: 'ORDERS',
        },
      ],
    },
    {
      key: 'marn-admin',
      title: t('Marn Admin'),
      items: [
        {
          text: t('Product Catalog'),
          icon: <Database className="w-5 h-5" />,
          path: '/product-catalog',
          moduleCode: 'PRODUCTS',
        },
        {
          text: t('Product Branches'),
          icon: <Package className="w-5 h-5" />,
          path: '/marn-admin/branches',
          moduleCode: 'BRANCHES',
        },
        {
          text: t('Content Management'),
          icon: <Sliders className="w-5 h-5" />,
          path: '/content-management',
          moduleCode: 'CONTENT_MANAGEMENT',
        },
      ],
    },
    {
      key: 'lyncs',
      title: t('Lyncs Admin'),
      items: [
        {
          text: t('Product Catalog'),
          icon: <Database className="w-5 h-5" />,
          path: '/lyncs/product-catalog',
          moduleCode: 'LYNCS_PRODUCT_CATALOG',
        },
        {
          text: t('Product Branches'),
          icon: <Package className="w-5 h-5" />,
          path: '/lyncs/branches',
          moduleCode: 'LYNCS_PRODUCT_BRANCHES',
        },
      ],
    },
    {
      key: 'support',
      title: t('Support'),
      items: [
        {
          text: t('Live Chat'),
          icon: <MessageCircle className="w-5 h-5" />,
          path: '/support',
          moduleCode: 'SUPPORT',
          badge: unreadTotal || 0,
        },
        {
          text: t('Rating & Reviews'),
          icon: <Star className="w-5 h-5" />,
          path: '/ratings-reviews',
          moduleCode: 'SUPPORT',
        },
      ],
    },
    {
      key: 'system',
      title: t('System'),
      items: [
        // {
        //   text: t('Anti-Fraud'),
        //   icon: <Shield className="w-5 h-5" />,
        //   path: '/anti-fraud',
        //   moduleCode: 'FRAUD',
        // },
        {
          text: t('Configuration'),
          // icon: <Settings className="w-5 h-5" />,
          icon: <CloudSync className='w-5 h-5' />,
          path: '/configuration',
          moduleCode: 'SETTINGS',
        },
        // {
        //   text: t('Integrations'),
        //   icon: <Puzzle className="w-5 h-5" />,
        //   path: '/integrations',
        //   moduleCode: 'INTEGRATIONS',
        // },
        {
          text: t('Audit Logs'),
          icon: <History className="w-5 h-5" />,
          path: '/audit-logs',
          moduleCode: 'AUDIT_LOGS',
        },
        {
          text: t('Fraud Detection'),
          icon: <ScanSearch className="w-5 h-5" />,
          path: '/fraud-detection',
          moduleCode: 'FRAUD',
        },
        {
          text: t('POS Sync Logs'),
          icon: <ScanSearch className="w-5 h-5" />,
          path: '/marn-sync',
          moduleCode: 'SYNC_LOGS',
        },
        {
          text: t('Settings'),
          icon: <Settings className="w-5 h-5" />,
          path: '/settings',
          moduleCode: 'SETTINGS',
        },
      ],
    },
    {
      key: 'documentation',
      title: t('Documentation Management'),
      items: [
        {
          text: t('FAQ Config'),
          icon: <HelpCircle className="w-5 h-5" />,
          path: '/faq-management',
          moduleCode: 'FAQ',
        },
        {
          text: t('Common Questions Config'),
          icon: <MessageCircleQuestionMark className="w-5 h-5" />,
          path: '/common-questions-config',
          moduleCode: 'FAQ',
        },
        {
          text: t('Terms & Conditions'),
          icon: <FileText className="w-5 h-5" />,
          path: '/terms-config',
          moduleCode: 'TERMS',
        },
        {
          text: t('VAT Certificate'),
          icon: <FileText className="w-5 h-5" />,
          path: '/vat-certificate',
          moduleCode: 'VAT_CERTIFICATE',
        },
      ],
    },
  ];

  // Filter nav sections based on access control
  const filteredNavSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasModuleAccess(item.moduleCode)),
    }))
    .filter((section) => section.items.length > 0);


  // Update open keys when location changes to show active section
  // useLayoutEffect runs before paint, preventing the visible "jump" on refresh
  useLayoutEffect(() => {
    // Find which section contains the current path
    const activeSection = filteredNavSections.findIndex(section =>
      section.items.some(item => item.path === location.pathname)
    );
    if (activeSection >= 0) {
      setOpenKeys([`section-${activeSection}`]);
    }
  }, [location.pathname]);

  // Map section keys to appropriate icons
  const getSectionIcon = (sectionKey) => {
    const iconMap = {
      'overview': <LayoutDashboard className="w-5 h-5" />,
      'member-management': <Users className="w-5 h-5" />,
      'engagement-growth': <Gift className="w-5 h-5" />,
      'automation-rules': <Mail className="w-5 h-5" />,
      'employee-management': <UserCircle className="w-5 h-5" />,
      'operations': <Store className="w-5 h-5" />,
      'marn-admin': <Database className="w-5 h-5" />,
      'lyncs': <CloudSync className="w-5 h-5" />,
      'support': <MessageCircle className="w-5 h-5" />,
      'documentation': <Book className="w-5 h-5" />,
      'system': <Settings className="w-5 h-5" />,
      'orders-management': <ShoppingBag className="w-5 h-5" />,
    };
    return iconMap[sectionKey] || <LayoutDashboard className="w-5 h-5" />;
  };

  // Convert nav sections to Ant Design menu items (collapsed - with accordion sections)
  // Wrap children in a `type: 'group'` so the section title renders as a header
  // at the top of the hover-popover (instead of suffixing every item).
  const collapsedMenuItems = filteredNavSections.map((section, sectionIdx) => {
    return {
      key: `collapsed-section-${sectionIdx}`,
      icon: getSectionIcon(section.key),
      label: section.title,
      children: [
        {
          key: `collapsed-section-${sectionIdx}-group`,
          type: 'group',
          label: (
            <div className="text-center font-semibold text-gray-900 text-sm pb-2 mb-1 border-b border-gray-200">
              {section.title}
            </div>
          ),
          children: section.items.map((item) => ({
            key: item.path,
            icon: item.badge ? (
              <Badge count={item.badge} size="small">
                {item.icon}
              </Badge>
            ) : (
              item.icon
            ),
            label: item.text,
            onClick: () => handleNavigate(item.path),
          })),
        },
      ],
    };
  });

  // Convert nav sections to Ant Design menu items (expanded - with collapsible sections)
  const expandedMenuItems = filteredNavSections.map((section, sectionIdx) => {
    return {
      key: `section-${sectionIdx}`,
      icon: getSectionIcon(section.key),
      label: section.title,
      children: section.items.map((item) => ({
        key: item.path,
        icon: item.badge ? (
          <Badge count={item.badge} size="small">
            {item.icon}
          </Badge>
        ) : (
          item.icon
        ),
        label: item.text,
        onClick: () => handleNavigate(item.path),
      })),
    };
  });

  // User menu items
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserCircle className="w-4 h-4" />,
      label: t('My Profile'),
      onClick: () => navigate('/settings?tab=5'),
    },
    ...(hasModuleAccess('SETTINGS') ? [{
      key: 'settings',
      icon: <Settings className="w-4 h-4" />,
      label: t('Settings'),
      onClick: () => navigate('/settings'),
    }] : []),
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogOut className="w-4 h-4" />,
      label: t('Logout'),
      danger: true,
      onClick: handleLogout,
    },
  ];

  // Quick actions menu
  const quickActionsItems = [
    {
      key: 'add-user',
      icon: <Users className="w-4 h-4" />,
      label: t('Add New User'),
      onClick: () => navigate('/employees'),
    },
    {
      key: 'award-points',
      icon: <Star className="w-4 h-4" />,
      label: t('Award Points'),
      onClick: () => navigate('/points?action=award'),
    },
    // {
    //   key: 'send-notification',
    //   icon: <Bell className="w-4 h-4" />,
    //   label: t('Send Notification'),
    //   onClick: () => navigate('/communication?action=send'),
    // },
  ];

  return (
    <Layout className="min-h-screen">
      {/* Collapsed Sidebar - Always visible with icons only */}
      <Sider
        width={collapsedDrawerWidth}
        collapsedWidth={collapsedDrawerWidth}
        collapsed={true}
        className={`bg-white hidden lg:block ${isRTL ? 'border-l' : 'border-r'} border-gray-200`}
        style={{
          position: 'fixed',
          [isRTL ? 'right' : 'left']: 0,
          top: 0,
          bottom: 0,
          zIndex: 1000,
        }}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Logo / Menu Toggle */}
          <div className="flex-shrink-0 flex items-center justify-center h-16 border-b border-gray-200">
            <Tooltip title="Open Menu" placement={isRTL ? 'left' : 'right'}>
              <button
                onClick={() => setDrawerOpen(true)}
                className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <MenuIcon className="w-6 h-6 text-primary-600" />
              </button>
            </Tooltip>
          </div>

          {/* Navigation Menu - Icons only */}
          <div className="flex-1 min-h-0 overflow-y-auto sidebar-scrollable">
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              items={collapsedMenuItems}
              className="border-r-0"
              inlineCollapsed={true}
            />
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 p-2 border-t border-gray-200">
            <div className="text-center">
              <img src={onlyLogo} alt="Address Cafe" className="w-8 h-8 mx-auto object-contain" />
            </div>
          </div>
        </div>
      </Sider>

      {/* Expanded Drawer - Opens when clicking menu icon */}
      <ConfigProvider direction={direction}>
        <Drawer
          key={`desktop-drawer-${direction}`}
          placement={isRTL ? 'right' : 'left'}
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={drawerWidth}
          styles={{ body: { padding: 0 } }}
          className="hidden lg:block"
          closable={false}
          destroyOnClose={true}
        >
          <div className="h-full flex flex-col overflow-hidden">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 h-16 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <img src={onlyLogo} alt="Address Cafe" className="w-8 h-8 object-contain" />
                <div>
                  <div className="text-base font-bold text-secondary leading-tight">
                    Address Cafe
                  </div>
                  <div className="text-[10px] text-gray-500">{t('Loyalty Platform')}</div>
                </div>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100"
              >
                {isRTL ? (
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                ) : (
                  <ChevronLeft className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Navigation Menu */}
            <div className="flex-1 min-h-0 overflow-y-auto sidebar-scrollable">
              <Menu
                mode="inline"
                selectedKeys={[location.pathname]}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                items={expandedMenuItems}
                className="border-r-0"
              />
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200">
              <div className="p-3 mb-3 bg-primary-50 rounded-lg">
                <p className="text-xs font-semibold text-primary-600">{t('Need Help?')}</p>
                <p className="text-xs text-gray-600 mt-1">{t('Check our documentation')}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-gray-500">{t('Powered by')}</p>
                <p className="text-xs font-bold text-primary-600 mt-1">Nexgensis</p>
                <p className="text-[9px] text-gray-500 mt-1">{APP_VERSION} • © 2026</p>
              </div>
            </div>
          </div>
        </Drawer>
      </ConfigProvider>

      {/* Main Layout */}
      <Layout className={isRTL ? '!mr-0 lg:!mr-[72px]' : '!ml-0 lg:!ml-[72px]'}>
        {/* Header */}
        <Header className="bg-white border-b border-gray-200 px-6 h-auto p-0 sticky top-0 z-[999]">
          <div className="flex items-center h-16">
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2 rounded hover:bg-gray-100 ${isRTL ? 'ml-4' : 'mr-4'}`}
            >
              <MenuIcon className="w-5 h-5" />
            </button>

            {/* App Name */}
            <div className={`flex items-center ${isRTL ? 'mr-2' : 'ml-2'}`}>
              <img src={addressCafeLogo} alt="Address Cafe" className="h-14 object-contain" />
            </div>

            <div className="flex-1" />

            {/* Right Side Icons */}
            <div className="flex items-center gap-1">
              {/* Quick Actions */}
              {/* <Dropdown menu={{ items: quickActionsItems }} trigger={['click']}>
                <Tooltip title={t('Quick Actions')}>
                  <button className="flex items-center justify-center p-2 rounded hover:bg-gray-100">
                    <Plus className="w-5 h-5" />
                  </button>
                </Tooltip>
              </Dropdown> */}

              {/* Notifications */}
              {/* <Tooltip title={t('Notifications')}>
                <button
                  className="flex items-center justify-center p-2 rounded hover:bg-gray-100 notification-button"
                  // onClick={() => navigate('/notifications')}
                >
                  <Badge count={5} size="small">
                    <Bell className="w-5 h-5" />
                  </Badge>
                </button>
              </Tooltip> */}

              {/* Language Switcher */}
              <LanguageSwitcher type="text" size="small" />

              {/* User Profile */}
              <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
                <button className="flex items-center justify-center p-2 rounded hover:bg-gray-100">
                  <Avatar size="small" className="bg-primary-600">
                    {user?.user_name?.charAt(0) || 'A'}
                  </Avatar>
                </button>
              </Dropdown>
            </div>
          </div>
        </Header>

        {/* Content */}
        <Content className="p-6 bg-background min-h-[calc(100vh-64px)]">
          <Outlet />

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              Built by <strong>Nexgensis</strong> for <strong>Address Cafe</strong>
            </p>
            <p className="text-xs text-gray-500 mt-1">{APP_VERSION} • © 2026</p>
          </div>
        </Content>
      </Layout>

      {/* Mobile Drawer */}
      <ConfigProvider direction={direction}>
        <Drawer
          key={`mobile-drawer-${direction}`}
          placement={isRTL ? 'right' : 'left'}
          onClose={() => setMobileOpen(false)}
          open={mobileOpen}
          className="lg:hidden"
          width={drawerWidth}
          styles={{ body: { padding: 0 } }}
          destroyOnClose={true}
        >
          <div className="h-full flex flex-col overflow-hidden">
            <div className="flex-shrink-0 flex items-center gap-2 px-4 h-16 border-b border-gray-200">
              <img src={onlyLogo} alt="Address Cafe" className="w-8 h-8 object-contain" />
              <div>
                <div className="text-base font-bold text-secondary">Address Cafe</div>
                <div className="text-[10px] text-gray-500">{t('Loyalty Platform')}</div>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto sidebar-scrollable">
              <Menu
                mode="inline"
                selectedKeys={[location.pathname]}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                items={expandedMenuItems}
                className="border-r-0"
              />
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200">
              <div className="text-center">
                <p className="text-[10px] text-gray-500">{t('Powered by')}</p>
                <p className="text-xs font-bold text-primary-600 mt-1">Nexgensis</p>
              </div>
            </div>
          </div>
        </Drawer>
      </ConfigProvider>
    </Layout>
  );
};

export default AdminLayout;
